import { useEffect, useState } from 'react';
import { PromotionEnumType } from '../../../enums/promotionEnum';
import { PromotionModel } from '../../../models/promotion';
import { MerChantModel } from '../../../models/merChantModel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { TagPromotionModel } from '../../../models/tagPromotion.model';
import { deletePromotion, getAllPromotion } from '../../../services/promotion';
import { getMerchantData } from '../../../services/merchant';
import { getAllTagPromotion } from '../../../services/tagPromotion';
import { useSearchParams } from 'react-router-dom';

const usePromotionController = () => {
  // -------------- useState & variable ------------- //

  const [promotionData, setPromotionData] = useState<PromotionModel[]>([]);
  const [branchData, setBranchData] = useState<MerChantModel[]>([]);
  const [tagPromotionData, setTagPromotionData] = useState<TagPromotionModel[]>([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [promotionId, setPromotionId] = useState<string>('');
  const [branchValue, setBranchValue] = useState<string>('All');
  const [selectedPromotionData, setSelectedPromotionData] = useState<PromotionModel>(null!);
  const [promotionType, setPromotionType] = useState<PromotionEnumType>(
    PromotionEnumType.buy_x_get_x
  );
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const isOpen = searchParams.get('openTagPromotion') === 'true';

  const adminRole = useSelector((state: RootState) => state.auth?.data?.role?.role_type);
  const assignMerchantToAdmin = useSelector((state: RootState) => state?.auth?.data?.merchants);
  // -------------- useState & variable ------------- //

  // ------------- GET DATA & API Manage ------------- //

  const handleFetchData = async (type: PromotionEnumType) => {
    try {
      setPromotionData([]);
      setLoadingData(true);
      const branchResponse = await getMerchantData();
      const resGetPromotion = await getAllPromotion({
        promotionType: type,
      });

      if (branchResponse?.length > 0) {
        const matchingMerchants = branchResponse.filter((merchant: any) =>
          assignMerchantToAdmin?.includes(merchant?.id)
        );
        if (adminRole === 'SUPER_ADMIN') {
          setBranchData(branchResponse);
        } else if (adminRole === 'ADMIN' && matchingMerchants.length > 0) {
          setBranchData(matchingMerchants);
        }
      }
      if (resGetPromotion?.length > 0) {
        const promotionData: any = resGetPromotion.map((item: PromotionModel) => {
          const populateBranch: MerChantModel[] = branchResponse.filter(
            (sub: MerChantModel) => sub.id === item.merchantId
          );
          return {
            ...item,
            branch: populateBranch.length > 0 ? populateBranch[0] : null,
          };
        });
        setPromotionData(promotionData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      window.alert('Error fetching data. Please try again later.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleGetData = async () => {
    try {
      setLoadingPage(true);
      await handleFetchData(promotionType);
      // console.log(promotionType);
    } catch (err) {
      // console.log(err);
    } finally {
      setLoadingPage(false);
    }
  };

  const handleGetTagPromotionData = async (): Promise<void> => {
    try {
      setLoadingData(true);
      const response = await getAllTagPromotion();
      const tagData = response;

      if (Array.isArray(tagData) && tagData.length > 0) {
        setTagPromotionData(tagData);
      } else {
        setTagPromotionData([]);
      }
    } catch (error) {
      console.error('Error fetching tag promotions:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeletePromotion = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await deletePromotion(promotionId);
      await handleGetData();
      setOpenDeleteForm(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
    }
  };

  // ------------- GET DATA & API Manage ------------- //

  // ----------- Function & Filter -------------- //

  const handleOpenTagPromotion = () => {
    searchParams.set('openTagPromotion', 'true');
    setSearchParams(searchParams);
  };

  const handleCloseTagPromotion = () => {
    searchParams.delete('openTagPromotion');
    setSearchParams(searchParams);
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setPromotionId(getId);
  };

  const handleOpenDetailForm = () => {
    setOpenForm(true);
    const getData = promotionData?.filter((data) => data?.id === promotionId)[0];
    setSelectedPromotionData(getData);
    // console.log("getData ==",getData);
  };

  const handleSelectPromotionType = async (newValue: PromotionEnumType) => {
    setPromotionType(newValue);
    setCurrentPage(1);
    setSearchValue('');
    await handleFetchData(newValue);
  };

  const filterDataByBranch: PromotionModel[] = promotionData?.filter((item: PromotionModel) => {
    if (branchValue === 'All') {
      return true;
    }
    return item?.merchantId === branchValue;
  });

  const filterData: PromotionModel[] = filterDataByBranch?.filter((item) =>
    item?.promotionName?.toLowerCase()?.includes(searchValue?.toLowerCase())
  );

  const rows: PromotionModel[] = filterData.slice((currentPage - 1) * 10, currentPage * 10);

  const countPagination = Math.ceil(filterData?.length / 10);
  const isSearchNotFound = !rows.length && !!searchValue;
  const countPromotion = filterDataByBranch.length;
  const allPromotionCount = promotionData?.length;

  // ----------- Function & Filter -------------- //

  // ----------- useEffect ------------- //

  useEffect(() => {
    handleGetData();
    // handleGetTagPromotionData();
    const initializeData = async () => {
      await handleFetchData(promotionType);
      await handleGetTagPromotionData();
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ----------- useEffect ------------- //

  return {
    openMenu,
    openForm,
    handleCloseForm: () => {
      setOpenForm(false);
      setOpenMenu(null);
      setSelectedPromotionData(null!);
    },
    handleOpenForm: () => setOpenForm(true),
    handleCloseMenu: () => setOpenMenu(null),
    handleOpenMenu,
    rows,
    currentPage,
    loadingData,
    handleChangePage: (_: any, newPage: any) => setCurrentPage(newPage),
    handleSelectedBranch: (newValue: string) => setBranchValue(newValue),
    branchValue,
    promotionType,
    handleSelectPromotionType,
    branchData,
    handleGetData,
    handleDeletePromotion,
    openDeleteForm,
    loadingDeleteButton,
    handleOpenDeleteForm: () => setOpenDeleteForm(true),
    handleCloseDeleteForm: () => setOpenDeleteForm(false),
    countPagination,
    handleSearchValue: (e: any) => {
      setSearchValue(e.target.value);
      setCurrentPage(1);
    },
    searchValue,
    isSearchNotFound,
    countPromotion,
    loadingPage,
    allPromotionCount,
    adminRole,
    openTagPromotion: isOpen,
    handleOpenTagPromotion,
    handleCloseTagPromotion,
    handleGetTagPromotionData,
    tagPromotionData,
    selectedPromotionData,
    handleOpenDetailForm,
  };
};
export default usePromotionController;
